(function () {
  'use strict';

  if (window.SuitsMoreDealOfWeek) {
    window.SuitsMoreDealOfWeek.update();
    return;
  }
  if (window.SuitsMoreDealOfWeekLoading) return;
  window.SuitsMoreDealOfWeekLoading = true;

  const CONFIG_ID = 'DealOfWeekConfig';
  const SURFACE_SELECTOR = '[data-deal-of-week-surface]';
  const COUNTDOWN_SELECTOR = '[data-deal-of-week-countdown]';
  const DYNAMIC_CONTAINER_SELECTOR = '[data-ajax-container].collection-listing, .collection-matrix';
  const EASTERN_TIME_ZONE = 'America/New_York';
  let intervalId = null;
  let observer = null;
  let countdownRoots = [];
  let hasCampaignSurface = false;
  let cachedConfigText = null;
  let cachedConfig = null;
  let cachedDeadlineKey = null;
  let cachedDeadline = null;
  const formatterCache = new Map();
  const countdownNodeCache = new WeakMap();
  const observedDynamicContainers = new WeakSet();
  const preflight = window.SuitsMoreDealOfWeekPreflight;

  if (preflight && preflight.configText) {
    cachedConfigText = preflight.configText;
    cachedConfig = preflight.config;
    cachedDeadlineKey = preflight.deadlineKey;
    cachedDeadline = preflight.deadline;
    if (preflight.timeZone && preflight.formatter) {
      formatterCache.set(preflight.timeZone, preflight.formatter);
    }
  }

  function readConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;

    if (node.textContent === cachedConfigText) return cachedConfig;
    cachedConfigText = node.textContent;

    try {
      cachedConfig = JSON.parse(node.textContent);
    } catch (error) {
      console.warn('Deal of the Week settings could not be read.', error);
      cachedConfig = null;
    }

    return cachedConfig;
  }

  function parseWallClock(dateValue, timeValue) {
    const dateMatch = String(dateValue || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const timeMatch = String(timeValue || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!dateMatch || !timeMatch) return null;

    const wallClock = {
      year: Number(dateMatch[1]),
      month: Number(dateMatch[2]),
      day: Number(dateMatch[3]),
      hour: Number(timeMatch[1]),
      minute: Number(timeMatch[2]),
      second: 0
    };

    if (
      wallClock.month < 1 || wallClock.month > 12 ||
      wallClock.day < 1 || wallClock.day > 31 ||
      wallClock.hour < 0 || wallClock.hour > 23 ||
      wallClock.minute < 0 || wallClock.minute > 59
    ) return null;

    return wallClock;
  }

  function zonedWallClockToTimestamp(wallClock, timeZone) {
    if (!wallClock) return null;

    let formatter = formatterCache.get(timeZone);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
      formatterCache.set(timeZone, formatter);
    }
    const desiredAsUtc = Date.UTC(
      wallClock.year,
      wallClock.month - 1,
      wallClock.day,
      wallClock.hour,
      wallClock.minute,
      wallClock.second
    );
    let timestamp = desiredAsUtc;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      const parts = Object.fromEntries(
        formatter.formatToParts(new Date(timestamp))
          .filter((part) => part.type !== 'literal')
          .map((part) => [part.type, Number(part.value)])
      );
      const displayedAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );
      const adjustment = desiredAsUtc - displayedAsUtc;
      timestamp += adjustment;
      if (adjustment === 0) break;
    }

    const validationParts = Object.fromEntries(
      formatter.formatToParts(new Date(timestamp))
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)])
    );
    const matches = Object.keys(wallClock).every((key) => validationParts[key] === wallClock[key]);
    return matches ? timestamp : null;
  }

  function getDeadline(config) {
    const timeZone = (config && config.timeZone) || EASTERN_TIME_ZONE;
    const deadlineKey = JSON.stringify([
      config && config.endDate,
      config && config.endTime,
      timeZone
    ]);
    if (deadlineKey === cachedDeadlineKey) return cachedDeadline;

    cachedDeadlineKey = deadlineKey;
    cachedDeadline = zonedWallClockToTimestamp(
      parseWallClock(config && config.endDate, config && config.endTime),
      timeZone
    );
    return cachedDeadline;
  }

  function getRemaining(deadline, now) {
    const totalSeconds = Math.max(0, Math.floor((deadline - now) / 1000));
    return {
      totalSeconds,
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };
  }

  function shouldMonitorCampaign(config, hasCountdown, hasSurface) {
    return Boolean(
      (config.countdownEnabled && hasCountdown) ||
      (config.autoHideExpired && hasSurface)
    );
  }

  function twoDigits(value) {
    return String(value).padStart(2, '0');
  }

  function setText(node, value) {
    const text = String(value);
    if (node && node.textContent !== text) node.textContent = text;
  }

  function getCountdownNodes(root) {
    let nodes = countdownNodeCache.get(root);
    if (!nodes) {
      nodes = {
        days: root.querySelector('[data-deal-of-week-days]'),
        hours: root.querySelector('[data-deal-of-week-hours]'),
        minutes: root.querySelector('[data-deal-of-week-minutes]'),
        seconds: root.querySelector('[data-deal-of-week-seconds]'),
        accessible: root.querySelector('[data-deal-of-week-countdown-accessible]')
      };
      countdownNodeCache.set(root, nodes);
    }
    return nodes;
  }

  function updateCountdown(root, remaining, expired, designMode) {
    if (expired && !designMode) {
      if (!root.hidden) root.hidden = true;
      return;
    }

    if (root.hidden) root.hidden = false;
    const nodes = getCountdownNodes(root);
    setText(nodes.days, twoDigits(remaining.days));
    setText(nodes.hours, twoDigits(remaining.hours));
    setText(nodes.minutes, twoDigits(remaining.minutes));
    setText(nodes.seconds, twoDigits(remaining.seconds));

    const accessible = nodes.accessible;
    if (accessible) {
      const accessibleText = expired
        ? 'Offer ended. This campaign is hidden from customers.'
        : `${remaining.days} days, ${remaining.hours} hours, ${remaining.minutes} minutes remaining.`;
      if (accessible.textContent !== accessibleText) accessible.textContent = accessibleText;
    }
    if (root.hasAttribute('data-deal-of-week-expired') !== expired) {
      root.toggleAttribute('data-deal-of-week-expired', expired);
    }
  }

  function normalizeAnnouncementBar(bar) {
    if (!bar) return;

    const announcements = Array.from(bar.querySelectorAll('.announcement')).filter((announcement) => !announcement.hidden);
    const allBackgrounds = Array.from(bar.querySelectorAll('.announcement-bg'));
    const visibleBackgrounds = [];

    announcements.forEach((announcement, index) => {
      const background = allBackgrounds.find((candidate) => (
        candidate.dataset.announcementBlockId === announcement.dataset.announcementBlockId
      ));
      if (background) {
        background.dataset.index = String(index);
        visibleBackgrounds.push(background);
      }
    });

    if (typeof bar.pauseAnnouncements === 'function') bar.pauseAnnouncements();
    bar.announcements = announcements;
    bar.backgrounds = visibleBackgrounds;
    bar.currentAnnouncement = 0;

    allBackgrounds.forEach((background) => background.classList.remove('is-active'));
    const controller = bar.querySelector('.announcement-bar__announcement-controller');
    if (controller) controller.hidden = announcements.length <= 1;

    if (announcements.length > 0 && typeof bar.setCurrentAnnouncement === 'function') {
      bar.setCurrentAnnouncement(0);
    }
    if (announcements.length > 1 && typeof bar.playAnnouncements === 'function') {
      bar.playAnnouncements();
    }
  }

  function updateCountdownTick() {
    const config = readConfig();
    if (!config || !config.enabled) {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      return;
    }

    const deadline = getDeadline(config);
    const now = Date.now();
    if (deadline === null || now >= deadline) {
      updateCampaign();
      return;
    }
    if (!config.countdownEnabled) return;

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const remaining = getRemaining(deadline, now);
    countdownRoots.forEach((countdown) => {
      updateCountdown(countdown, remaining, false, designMode);
    });
  }

  function observeDynamicContainers() {
    if (!observer) observer = new MutationObserver(start);
    document.querySelectorAll(DYNAMIC_CONTAINER_SELECTOR).forEach((container) => {
      if (observedDynamicContainers.has(container)) return;
      observedDynamicContainers.add(container);
      observer.observe(container, { childList: true });
    });
  }

  function updateCampaign() {
    const config = readConfig();
    if (!config || !config.enabled) {
      document.documentElement.classList.remove(
        'deal-of-week-surface-ready',
        'deal-of-week-countdown-ready',
        'deal-of-week-is-expired'
      );
      return;
    }

    const deadline = getDeadline(config);
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const now = Date.now();
    const expired = deadline !== null && now >= deadline;
    const futureDeadline = deadline !== null && now < deadline;
    const shouldHide = Boolean(config.autoHideExpired && !futureDeadline && !designMode);
    const surfaceReady = !shouldHide;
    const countdownReady = Boolean(config.countdownEnabled && deadline !== null && (!expired || designMode));
    const surfaces = document.querySelectorAll(SURFACE_SELECTOR);
    const announcementBars = new Set();
    countdownRoots = Array.from(document.querySelectorAll(COUNTDOWN_SELECTOR));
    hasCampaignSurface = surfaces.length > 0;

    if (document.documentElement.classList.contains('deal-of-week-is-expired') !== shouldHide) {
      document.documentElement.classList.toggle('deal-of-week-is-expired', shouldHide);
    }
    document.documentElement.classList.toggle('deal-of-week-surface-ready', surfaceReady);
    document.documentElement.classList.toggle('deal-of-week-countdown-ready', countdownReady);

    surfaces.forEach((surface) => {
      const announcementState = shouldHide ? 'hidden' : 'visible';
      const announcementNeedsSync = surface.getAttribute('data-deal-of-week-announcement-state') !== announcementState;
      const expiredPreview = expired && designMode;
      if (surface.hidden !== shouldHide) surface.hidden = shouldHide;
      if (surface.hasAttribute('data-deal-of-week-expired-preview') !== expiredPreview) {
        surface.toggleAttribute('data-deal-of-week-expired-preview', expiredPreview);
      }
      if (announcementNeedsSync && surface.hasAttribute('data-deal-of-week-announcement')) {
        surface.setAttribute('data-deal-of-week-announcement-state', announcementState);
        announcementBars.add(surface.closest('announcement-bar'));
      }
    });

    announcementBars.forEach(normalizeAnnouncementBar);

    countdownRoots.forEach((countdown) => {
      if (!config.countdownEnabled || deadline === null) {
        if (!countdown.hidden) countdown.hidden = true;
        return;
      }
      updateCountdown(countdown, getRemaining(deadline, now), expired, designMode);
    });

    if (intervalId !== null && (expired || deadline === null)) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  function start() {
    const config = readConfig();
    if (!config || !config.enabled) return;

    updateCampaign();
    const deadline = getDeadline(config);
    const shouldMonitor = shouldMonitorCampaign(
      config,
      countdownRoots.length > 0,
      hasCampaignSurface
    );
    if (
      shouldMonitor &&
      deadline !== null &&
      Date.now() < deadline &&
      intervalId === null
    ) {
      intervalId = window.setInterval(updateCountdownTick, 1000);
    }

    observeDynamicContainers();
  }

  window.SuitsMoreDealOfWeek = {
    parseWallClock,
    zonedWallClockToTimestamp,
    getDeadline,
    getRemaining,
    shouldMonitorCampaign,
    tick: updateCountdownTick,
    update: start
  };
  window.SuitsMoreDealOfWeekLoading = false;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('on:cart:after-merge', start);
})();
